/*
  <ia-gorgias-bridge> — the theme side of the Gorgias chat widget. Rendered by
  snippets/ia-gorgias-chat.liquid right after the vendor loaders.

  - An add made from inside the chat (a campaign or AI product card) re-reads
    the cart through iaCartAPI and opens the drawer. Gorgias posts the add
    itself (/cart/add.js or /cart/update.js, no sections) and then fires the
    chat widget's `cart:updated` (window.GorgiasChat.on). The older
    GorgiasBridge.subscribeToCartUpdated only listens for a
    `gorgias:cart-updated` document event that the 2026-09 bundles never
    dispatch, so that event is kept as a fallback only. The chat iframes sit
    under the drawer by the z-index pin in ia-custom.css.
  - When a chat campaign closes, the page ignores pointers for a beat
    (data-guard-ms): the tap that closed the campaign iframe otherwise lands
    on whatever sat behind it.
*/
import { openCart } from 'ia-util-helpers'

const CAMPAIGN_IFRAME_ID = 'chat-campaigns'
// At or below this the campaign iframe is collapsed, showing nothing.
const OPEN_MIN_HEIGHT = 10
const GUARD_MS = 400
const CHAT_CART_EVENT = 'cart:updated'
const BRIDGE_CART_EVENT = 'gorgias:cart-updated'

class IaGorgiasBridge extends HTMLElement {
  connectedCallback() {
    this.guardMs = parseInt(this.dataset.guardMs, 10) || GUARD_MS
    this.onCartUpdated = this.handleCartUpdated.bind(this)
    document.addEventListener(BRIDGE_CART_EVENT, this.onCartUpdated)
    this.connectChat()
    this.watchCampaigns()
  }

  disconnectedCallback() {
    document.removeEventListener(BRIDGE_CART_EVENT, this.onCartUpdated)
    // Before init the GorgiasChat proxy throws on any property but init.
    if (this.chatBound) window.GorgiasChat.off(CHAT_CART_EVENT, this.onCartUpdated)
    this.mountObserver?.disconnect()
    this.sizeObserver?.disconnect()
    clearTimeout(this.guardTimeout)
    document.body.style.pointerEvents = ''
  }

  // The loader stubs window.GorgiasChat before the bundle; init() resolves once the widget is up.
  async whenChatReady() {
    if (!window.GorgiasChat) {
      await new Promise((resolve) => {
        window.addEventListener('gorgias-widget-loaded', resolve, { once: true })
      })
    }
    return window.GorgiasChat.init()
  }

  async connectChat() {
    try {
      await this.whenChatReady()
      window.GorgiasChat.on(CHAT_CART_EVENT, this.onCartUpdated)
      this.chatBound = true
    } catch (error) {
      console.warn('[ia-gorgias-bridge] chat setup failed:', error.message)
    }
  }

  // Same shape as the Swym hook in ia-wishlist: repaint from the server, then show the bag.
  handleCartUpdated() {
    Promise.resolve(window.iaCartAPI?.refresh?.()).finally(() => {
      openCart()
    })
  }

  // The campaign iframe mounts whenever the widget decides to; wait for it once.
  watchCampaigns() {
    const iframe = document.getElementById(CAMPAIGN_IFRAME_ID)
    if (iframe) return this.guardClicks(iframe)
    this.mountObserver = new MutationObserver(() => {
      const mounted = document.getElementById(CAMPAIGN_IFRAME_ID)
      if (!mounted) return
      this.mountObserver.disconnect()
      this.guardClicks(mounted)
    })
    this.mountObserver.observe(document.documentElement, { childList: true, subtree: true })
  }

  // The widget opens and closes a campaign by resizing its iframe.
  guardClicks(iframe) {
    if (typeof ResizeObserver === 'undefined') return
    let wasOpen = iframe.getBoundingClientRect().height > OPEN_MIN_HEIGHT
    this.sizeObserver = new ResizeObserver(() => {
      const isOpen = iframe.getBoundingClientRect().height > OPEN_MIN_HEIGHT
      if (wasOpen && !isOpen) this.suspendPointer()
      wasOpen = isOpen
    })
    this.sizeObserver.observe(iframe)
  }

  suspendPointer() {
    document.body.style.pointerEvents = 'none'
    clearTimeout(this.guardTimeout)
    this.guardTimeout = setTimeout(() => {
      document.body.style.pointerEvents = ''
    }, this.guardMs)
  }
}

if (!customElements.get('ia-gorgias-bridge')) {
  customElements.define('ia-gorgias-bridge', IaGorgiasBridge)
}
